import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Coins,
  HandCoins,
  ScrollText,
  Search,
  ShieldCheck,
  Undo2,
  X,
} from "lucide-react";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import {
  cn,
  parseMoneyToCents,
  displayPrice,
  formatDate,
  getOrderTypeLabel,
} from "@/lib/utils";
import { useRecordMovementMutation } from "@/redux/services/pos/registerSession.api";
import { useGetOrdersQuery } from "@/redux/services/order/order.api";
import { Order } from "@/redux/services/order/order.type";
import { useDebounce } from "@/hooks/useDebounce";
import { getOutstandingRefund } from "@/lib/refunds";
import {
  CashMovementDirection,
  CashMovementType,
} from "@/redux/services/pos/pos.type";
import { getApiErrorMessage, translateApiErrorText } from "@/lib/apiErrors";

interface RecordMovementModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  sessionUuid: string;
  currency: string;
  initialType?: CashMovementType;
  /** Pre-fill the amount in cents (e.g. an order's overpaid balance). */
  initialAmount?: number;
  /** Attach an order IRI (`/api/orders/{uuid}`) to the movement — e.g. a refund. */
  orderIri?: string;
  /** Order label shown as context when an order is attached. */
  orderLabel?: string;
}

interface TypeOption {
  value: CashMovementType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  effect: "positive" | "negative" | "variable";
}

const TYPE_OPTIONS: TypeOption[] = [
  {
    value: "pay_in",
    label: "Approvisionnement",
    description: "Fond ajouté à la caisse (réassort, change supplémentaire).",
    icon: ArrowDownToLine,
    effect: "positive",
  },
  {
    value: "pay_out",
    label: "Sortie de caisse",
    description: "Espèces sorties pour une dépense (fournisseur, achat…).",
    icon: ArrowUpFromLine,
    effect: "negative",
  },
  {
    value: "tip_in",
    label: "Pourboires reçus",
    description: "Pourboires ajoutés à la caisse.",
    icon: Coins,
    effect: "positive",
  },
  {
    value: "tip_out",
    label: "Pourboires retirés",
    description: "Pourboires reversés au personnel.",
    icon: HandCoins,
    effect: "negative",
  },
  {
    value: "refund",
    label: "Remboursement",
    description: "Remboursement en espèces à un client.",
    icon: Undo2,
    effect: "negative",
  },
  {
    value: "correction",
    label: "Correction",
    description:
      "Ajustement manuel après recomptage (caisse en excédent / déficit).",
    icon: ScrollText,
    effect: "variable",
  },
  {
    value: "cash_drop",
    label: "Dépôt coffre",
    description: "Espèces déposées au coffre / retirées pour sécurité.",
    icon: ShieldCheck,
    effect: "negative",
  },
];

const SAVE_ERROR = "Une erreur s'est produite lors de l'enregistrement.";

// French text for one violation (shared map, SFRESTO-163); never the raw key.
const translateViolation = (key: string): string =>
  translateApiErrorText(key) ?? SAVE_ERROR;

const requiresReason = (type: CashMovementType): boolean =>
  type === "pay_out" || type === "correction";

interface AttachedOrder {
  id: string;
  label: string;
}

/**
 * Search-and-pick the order a refund settles. Scoped to the session's
 * restaurant, so the chosen order always belongs to it (avoids the backend's
 * `order_not_in_restaurant` rejection).
 */
const RefundOrderPicker = ({
  restaurantId,
  value,
  onSelect,
  currency,
}: {
  restaurantId: string;
  value: AttachedOrder | null;
  onSelect: (order: Order | null) => void;
  currency: string;
}) => {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const debounced = useDebounce(query.trim(), 300);

  const { data, isFetching } = useGetOrdersQuery(
    {
      restaurantId,
      query: {
        orderNumber: debounced,
        limit: 6,
        "order[createdAt]": "desc",
      },
    },
    { skip: debounced.length === 0 || !!value },
  );
  const results = data?.results ?? [];
  const showDropdown = focused && !value && debounced.length > 0;

  if (value) {
    return (
      <div>
        <p className="block text-sm font-medium text-card-foreground mb-1">
          Commande remboursée
        </p>
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm">
          <span className="font-medium text-card-foreground">
            Commande #{value.label}
          </span>
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="text-muted-foreground transition-colors hover:text-destructive"
            aria-label="Retirer la commande"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <label
        htmlFor="refundOrder"
        className="block text-sm font-medium text-card-foreground mb-1"
      >
        Commande remboursée{" "}
        <span className="text-xs font-normal text-muted-foreground">
          (optionnel)
        </span>
      </label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          id="refundOrder"
          type="search"
          autoComplete="off"
          placeholder="Rechercher par numéro de commande…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          className="w-full rounded-lg border border-border py-2.5 pl-9 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
      {showDropdown && (
        <div className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-lg border border-border bg-white shadow-lg">
          {isFetching ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">Recherche…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              Aucune commande trouvée.
            </p>
          ) : (
            results.map((order) => {
              const place =
                order.orderType === "dine_in" && order.restaurantTable?.code
                  ? `Table ${order.restaurantTable.code}`
                  : order.orderType
                    ? getOrderTypeLabel(order.orderType)
                    : null;
              return (
                <button
                  key={order.id}
                  type="button"
                  // mousedown (not click) so the input's blur doesn't cancel it
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(order);
                    setQuery("");
                  }}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/60"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-card-foreground">
                      #{order.orderNumber}
                      {order.dailySequence != null
                        ? ` · N°${order.dailySequence}`
                        : ""}
                      {place ? (
                        <span className="font-normal text-muted-foreground">
                          {" "}
                          · {place}
                        </span>
                      ) : null}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {formatDate(new Date(order.createdAt), true, true)}
                    </span>
                  </span>
                  <span className="flex-shrink-0 text-xs text-muted-foreground">
                    {displayPrice(order.paidAmount ?? 0, currency)} payé
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const RecordMovementModal = ({
  isOpen,
  onClose,
  restaurantId,
  sessionUuid,
  currency,
  initialType,
  initialAmount,
  orderIri,
  orderLabel,
}: RecordMovementModalProps) => {
  // Opened from an order (its drawer's "Rembourser"), the movement can only
  // be the refund that settles it: other types would be recorded against the
  // order without settling it (SFRESTO-127).
  const lockedType: CashMovementType | null = orderIri ? "refund" : null;
  const typeOptions = lockedType
    ? TYPE_OPTIONS.filter((o) => o.value === lockedType)
    : TYPE_OPTIONS;
  const [type, setType] = useState<CashMovementType | null>(
    lockedType ?? initialType ?? null,
  );
  const [amount, setAmount] = useState<string>(
    initialAmount && initialAmount > 0 ? String(initialAmount / 100) : "",
  );
  const [reason, setReason] = useState<string>("");
  const [direction, setDirection] = useState<CashMovementDirection | null>(
    null,
  );
  // Order attached when picked from the picker (manual refund flow). A prop
  // `orderIri` takes precedence (refund launched from an order's drawer).
  const [orderRef, setOrderRef] = useState<AttachedOrder | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Only a refund settles an order (the API counts only REFUND movements in
  // the order's refunded amount), so no other type carries the order.
  const attachedOrderIri =
    type === "refund"
      ? (orderIri ?? (orderRef ? `/api/orders/${orderRef.id}` : undefined))
      : undefined;

  const handleSelectOrder = (order: Order | null) => {
    if (!order) {
      setOrderRef(null);
      return;
    }
    setOrderRef({
      id: order.id,
      label: String(order.dailySequence ?? order.orderNumber),
    });
    // Pre-fill the amount with what the order still owes back, if empty.
    if (!amount.trim()) {
      const outstanding = getOutstandingRefund(order);
      if (outstanding > 0) setAmount(String(outstanding / 100));
    }
  };

  const [recordMovement, { isLoading }] = useRecordMovementMutation();

  const selectedType = useMemo(
    () => TYPE_OPTIONS.find((o) => o.value === type) ?? null,
    [type],
  );

  const amountCents = useMemo(() => {
    const cents = parseMoneyToCents(amount);
    if (cents === null || cents <= 0) return 0;
    return cents;
  }, [amount]);

  const signedPreview = useMemo(() => {
    if (!selectedType || amountCents === 0) return null;
    if (selectedType.effect === "positive") return amountCents;
    if (selectedType.effect === "negative") return -amountCents;
    if (direction === "credit") return amountCents;
    if (direction === "debit") return -amountCents;
    return null;
  }, [selectedType, amountCents, direction]);

  const resetForm = () => {
    setType(lockedType);
    setAmount("");
    setReason("");
    setDirection(null);
    setOrderRef(null);
    setFieldErrors({});
    setSubmitError(null);
  };

  const handleClose = () => {
    if (isLoading) return;
    resetForm();
    onClose();
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!type) {
      errors.type = "Sélectionnez un type de mouvement.";
    }
    if (amountCents <= 0) {
      errors.amount = "Le montant doit être supérieur à zéro.";
    }
    if (type && requiresReason(type) && !reason.trim()) {
      errors.reason = "La raison est obligatoire pour ce type de mouvement.";
    }
    if (type === "correction" && !direction) {
      errors.direction = "Choisissez le sens de la correction.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate() || !type) return;

    try {
      await recordMovement({
        restaurantId,
        sessionUuid,
        data: {
          type,
          amount: amountCents,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(type === "correction" && direction
            ? { direction }
            : {}),
          ...(attachedOrderIri ? { order: attachedOrderIri } : {}),
        },
      }).unwrap();

      toast.success("Mouvement enregistré");
      resetForm();
      onClose();
    } catch (error: unknown) {
      const data = (
        error as {
          data?: {
            violations?: Array<{ propertyPath: string; message: string }>;
            detail?: string;
          };
        }
      )?.data;
      const violations = data?.violations;
      if (violations?.length) {
        const nextErrors: Record<string, string> = {};
        const unmapped: string[] = [];
        for (const v of violations) {
          const message = translateViolation(v.message);
          if (v.propertyPath && v.propertyPath in fieldMap) {
            nextErrors[fieldMap[v.propertyPath]] = message;
          } else {
            unmapped.push(message);
          }
        }
        setFieldErrors(nextErrors);
        if (unmapped.length) setSubmitError([...new Set(unmapped)].join(" · "));
      } else {
        setSubmitError(getApiErrorMessage(error, SAVE_ERROR));
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Enregistrer un mouvement"
      size="large"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {orderIri && (
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-card-foreground">
            Rattaché à la commande
            {orderLabel ? ` #${orderLabel}` : ""}.
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-card-foreground mb-2">
            Type de mouvement
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {typeOptions.map((option) => {
              const Icon = option.icon;
              const isActive = type === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setType(option.value);
                    setFieldErrors((prev) => ({ ...prev, type: "" }));
                    if (option.value !== "correction") setDirection(null);
                    if (option.value !== "refund") setOrderRef(null);
                  }}
                  className={cn(
                    "flex items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors",
                    isActive
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border hover:border-primary/40 hover:bg-muted/40",
                  )}
                >
                  <span
                    className={cn(
                      "flex-shrink-0 h-9 w-9 rounded-md flex items-center justify-center",
                      option.effect === "positive"
                        ? "bg-success/10 text-success"
                        : option.effect === "negative"
                        ? "bg-destructive/10 text-destructive"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-card-foreground">
                      {option.label}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {option.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          {fieldErrors.type && (
            <p className="text-xs text-destructive mt-2">{fieldErrors.type}</p>
          )}
        </div>

        {/* Refunds can be tied to the order they settle (manual flow only — when
            launched from an order's drawer the order is already attached). */}
        {type === "refund" && !orderIri && (
          <RefundOrderPicker
            restaurantId={restaurantId}
            value={orderRef}
            onSelect={handleSelectOrder}
            currency={currency}
          />
        )}

        <div>
          <label
            htmlFor="movementAmount"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Montant
          </label>
          <input
            id="movementAmount"
            type="text"
            inputMode="decimal"
            placeholder="0"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setFieldErrors((prev) => ({ ...prev, amount: "" }));
            }}
            className={cn(
              "w-full rounded-lg border px-4 py-3 text-lg focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary",
              fieldErrors.amount ? "border-destructive" : "border-border",
            )}
          />
          {fieldErrors.amount && (
            <p className="text-xs text-destructive mt-1">
              {fieldErrors.amount}
            </p>
          )}
        </div>

        {type === "correction" && (
          <div>
            <p className="block text-sm font-medium text-card-foreground mb-1">
              Sens de la correction
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  setDirection("credit");
                  setFieldErrors((prev) => ({ ...prev, direction: "" }));
                }}
                className={cn(
                  "rounded-lg border px-3 py-3 text-sm font-medium transition-colors",
                  direction === "credit"
                    ? "border-success bg-success/10 text-success"
                    : "border-border hover:border-success/40 text-card-foreground",
                )}
              >
                Crédit (+)
                <span className="block text-xs font-normal opacity-80">
                  La caisse contient plus que prévu.
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setDirection("debit");
                  setFieldErrors((prev) => ({ ...prev, direction: "" }));
                }}
                className={cn(
                  "rounded-lg border px-3 py-3 text-sm font-medium transition-colors",
                  direction === "debit"
                    ? "border-destructive bg-destructive/10 text-destructive"
                    : "border-border hover:border-destructive/40 text-card-foreground",
                )}
              >
                Débit (−)
                <span className="block text-xs font-normal opacity-80">
                  La caisse contient moins que prévu.
                </span>
              </button>
            </div>
            {fieldErrors.direction && (
              <p className="text-xs text-destructive mt-1">
                {fieldErrors.direction}
              </p>
            )}
          </div>
        )}

        {type && requiresReason(type) && (
          <div>
            <label
              htmlFor="movementReason"
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Raison
            </label>
            <textarea
              id="movementReason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setFieldErrors((prev) => ({ ...prev, reason: "" }));
              }}
              rows={2}
              maxLength={255}
              placeholder="Ex: Paiement fournisseur Boulangerie X"
              className={cn(
                "w-full rounded-lg border px-4 py-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary",
                fieldErrors.reason ? "border-destructive" : "border-border",
              )}
            />
            {fieldErrors.reason && (
              <p className="text-xs text-destructive mt-1">
                {fieldErrors.reason}
              </p>
            )}
          </div>
        )}

        {type && !requiresReason(type) && (
          <div>
            <label
              htmlFor="movementReasonOptional"
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Note (optionnel)
            </label>
            <textarea
              id="movementReasonOptional"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={255}
              placeholder="Précision sur ce mouvement"
              className="w-full rounded-lg border border-border px-4 py-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        )}

        {signedPreview != null && (
          <div className="rounded-lg bg-muted/50 p-4 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Impact sur les espèces attendues
            </span>
            <span
              className={cn(
                "font-semibold tabular-nums",
                signedPreview > 0
                  ? "text-success"
                  : signedPreview < 0
                  ? "text-destructive"
                  : "text-card-foreground",
              )}
            >
              {signedPreview > 0 ? "+" : "−"}
              {displayPrice(Math.abs(signedPreview), currency)}
            </span>
          </div>
        )}

        {submitError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {submitError}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={isLoading} isLoading={isLoading}>
            Enregistrer
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const fieldMap: Record<string, string> = {
  type: "type",
  amount: "amount",
  reason: "reason",
  direction: "direction",
};

export default RecordMovementModal;
